import { useCallback, useEffect, useState } from "react";

import Modal from "./Modal";
import Button from "../Buttons/Button";
import { defineMessages } from "react-intl";
import { useFormatMessage } from "../Intl/Context";

const ConfirmationModal = ({ closeOnConfirm = true, isLoading = false, ...props }) => {
  const { title, openRef, closeRef, onConfirm, children } = props;

  // Allow either passing the message content as a "message" property, or through the
  // children prop of the component if we need more structured content
  const message = props.message || children;

  const t = useFormatMessage();
  const confirmButtonLabel =
    props.confirmButtonLabel || t(intlMessages.confirmButtonLabel);
  const cancelButtonLabel = props.cancelButtonLabel || t(intlMessages.cancelButtonLabel);

  const [isOpen, setIsOpen] = useState(false);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  // This is where we make the open and close functions for the modal availble to the
  // parent component, so it can control when to open and close it.
  //
  // Manual opening is always needed to display the confirmation modal, as it doesn't
  // show when rendered.
  //
  // Manual closing is useful in combination with the `closeOnConfirm` prop set to
  // `false`, for example to allow closing the modal after a request is completed, and
  // not immediately when the confirm button is clicked
  useEffect(() => {
    if (openRef) openRef.current = openModal;
    if (closeRef) closeRef.current = closeModal;
  }, [closeModal, closeRef, openModal, openRef]);

  const handleConfirmButtonClicked = useCallback(() => {
    onConfirm && onConfirm();
    closeOnConfirm && closeModal();
  }, [closeModal, closeOnConfirm, onConfirm]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={true}
      shouldCloseOnOverlayClick={true}
      title={title}
      onRequestClose={closeModal}
    >
      <div
        className="box padding-x-lg padding-y-md margin-bottom-xs"
        dangerouslySetInnerHTML={{ __html: message }}
      />

      <div className="modal-actions modal-buttons-container">
        <Button
          type="button"
          className="btn btn-primary btn-block-md modal-action box-shadow"
          onClick={handleConfirmButtonClicked}
          label={confirmButtonLabel}
          iconClassName="icon-validate"
          iconPosition="after"
          data-loading={isLoading}
          disabled={isLoading}
        />

        <Button
          type="button"
          onClick={closeModal}
          className="btn btn-primary-inverse btn-block-md box-shadow-lg modal-action"
          label={cancelButtonLabel}
          iconClassName="icon-suppr"
          iconPosition="after"
          data-loading={isLoading}
          disabled={isLoading}
        />
      </div>
    </Modal>
  );
};

export default ConfirmationModal;

const intlMessages = defineMessages({
  confirmButtonLabel: {
    id: "report.confirmButtonLabel",
    defaultMessage: "Confirmer"
  },
  cancelButtonLabel: {
    id: "report.cancelButtonLabel",
    defaultMessage: "Annuler"
  }
});
